<template>
    <v-dialog
        :value="dialog"
        @input="$emit('close')"
        max-width="1600px"
        z-index="9999"
        persistent
    >
        <v-card>
            <v-card-title class="headline">
                Relate Invoice to Entries
            </v-card-title>
            <v-card-text>
                <div class="mb-4">
                    <h4>
                        Invoice:
                        <a
                            v-if="invoice?.fileName"
                            href="#"
                            @click.prevent="$emit('openInvoice', invoice)"
                            style="color: #1976d2; cursor: pointer;"
                        >
                            {{ invoice.consecutive || invoice.fileName }}
                        </a>
                        <span v-else>No file</span>
                    </h4>
                    <p class="text-caption">
                        Select the entries you want to relate this invoice to
                    </p>
                </div>

                <v-data-table
                    :headers="entryRelationHeaders"
                    :items="visibleEntries"
                    :items-per-page="-1"
                    :hide-default-footer="true"
                    class="elevation-1"
                    height="auto"
                >
                    <template v-slot:[`header.itemDescription`]="{ header }">
                        <v-text-field
                            :label="header.text"
                            v-model="itemFilter"
                            dense
                            hide-details
                            class="pt-2"
                            @click.stop
                        />
                    </template>
                    <template v-slot:[`header.supplier`]="{ header }">
                        <v-text-field
                            :label="header.text"
                            v-model="supplierFilter"
                            dense
                            hide-details
                            class="pt-2"
                            @click.stop
                        />
                    </template>
                    <template v-slot:[`header.unitPrice`]="{ header }">
                        <span
                            class="d-inline-flex align-center"
                            style="cursor: pointer; user-select: none;"
                            @click="toggleSort('unitPrice')"
                        >
                            {{ header.text }}
                            <v-icon
                                small
                                class="ml-1"
                                :color="
                                    sortBy === 'unitPrice' ? 'primary' : 'grey'
                                "
                            >
                                {{ sortIcon('unitPrice') }}
                            </v-icon>
                        </span>
                    </template>
                    <template v-slot:[`header.entryDate`]="{ header }">
                        <span
                            class="d-inline-flex align-center"
                            style="cursor: pointer; user-select: none;"
                            @click="toggleSort('entryDate')"
                        >
                            {{ header.text }}
                            <v-icon
                                small
                                class="ml-1"
                                :color="
                                    sortBy === 'entryDate' ? 'primary' : 'grey'
                                "
                            >
                                {{ sortIcon('entryDate') }}
                            </v-icon>
                        </span>
                    </template>
                    <template v-slot:no-data>
                        <span class="text-caption">
                            {{
                                itemFilter || supplierFilter
                                    ? 'No entries match the selected filters'
                                    : 'No entries available'
                            }}
                        </span>
                    </template>
                    <template v-slot:[`item.itemDescription`]="{ item }">
                        <div class="d-flex align-center" style="height: 100%;">
                            <span>{{ item.itemDescription }}</span>
                        </div>
                    </template>

                    <template v-slot:[`item.supplier`]="{ item }">
                        <div class="d-flex align-center" style="height: 100%;">
                            <span>{{ item.supplierName }}</span>
                        </div>
                    </template>

                    <template v-slot:[`item.entryQuantity`]="{ item }">
                        <div class="d-flex align-center" style="height: 100%;">
                            <span>{{ item.entryQuantity }}</span>
                        </div>
                    </template>

                    <template v-slot:[`item.availableQuantity`]="{ item }">
                        <div class="d-flex align-center" style="height: 100%;">
                            <span>{{ item.availableQuantity }}</span>
                        </div>
                    </template>

                    <template v-slot:[`item.selectedQuantity`]="{ item }">
                        <div class="d-flex align-center" style="height: 100%;">
                            <span>{{ item.selectedQuantity || 0 }}</span>
                        </div>
                    </template>
                    <template v-slot:[`item.unitPrice`]="{ item }">
                        <div class="d-flex align-center" style="height: 100%;">
                            <p class="my-0">
                                {{ formatNumber(item.unitPrice) }}
                            </p>
                        </div>
                    </template>
                    <template v-slot:[`item.entryDate`]="{ item }">
                        <div class="d-flex align-center" style="height: 100%;">
                            <span>{{ formatEntryDate(item.entryDate) }}</span>
                        </div>
                    </template>

                    <template v-slot:[`item.select`]="{ item }">
                        <div
                            class="d-flex align-center justify-center"
                            :style="{
                                height: '100%',
                                cursor: isInvoiceRejected
                                    ? 'default'
                                    : 'pointer',
                            }"
                            @click.stop.prevent="
                                isInvoiceRejected
                                    ? null
                                    : openQuantityDialog(item)
                            "
                        >
                            <v-icon
                                :color="selectCheckboxColor(item)"
                                size="24"
                            >
                                {{
                                    isEntrySelected(item.entryId)
                                        ? 'mdi-checkbox-marked'
                                        : 'mdi-checkbox-blank-outline'
                                }}
                            </v-icon>
                        </div>
                    </template>

                    <template v-slot:[`item.sap`]="{ item }">
                        <div
                            class="d-flex align-center justify-center"
                            style="height: 100%;"
                        >
                            <v-checkbox
                                :input-value="sapCheckboxValue(item)"
                                @change="updateEntrySapStatus(item, $event)"
                                :disabled="isInvoiceRejected"
                                color="primary"
                                hide-details
                                class="ma-0 pa-0"
                            ></v-checkbox>
                        </div>
                    </template>
                    <template v-slot:[`item.accountant`]="{ item }">
                        <div
                            class="d-flex align-center justify-center"
                            style="height: 100%;"
                        >
                            <v-checkbox
                                :input-value="accountantCheckboxValue(item)"
                                @change="
                                    updateEntryAccountantStatus(item, $event)
                                "
                                :disabled="
                                    isInvoiceRejected ||
                                        !checkWithPoAccountingPermission
                                "
                                color="primary"
                                hide-details
                                class="ma-0 pa-0"
                            ></v-checkbox>
                        </div>
                    </template>
                    <template v-slot:[`item.relatedInvoices`]="{ item }">
                        <div
                            class="d-flex flex-column"
                            style="min-height: 100%; width: 100%;"
                        >
                            <div
                                v-for="relatedInvoice in getRelatedInvoicesForEntry(
                                    item
                                )"
                                :key="
                                    relatedInvoice.fileName || relatedInvoice.id
                                "
                                class="d-flex align-center"
                                style="
                                    margin-bottom: 4px;
                                    min-height: 20px;
                                    line-height: 1.2;
                                    width: 100%;
                                "
                            >
                                <a
                                    href="#"
                                    @click.prevent="
                                        $emit('openInvoice', relatedInvoice)
                                    "
                                    class="text-decoration-none"
                                    style="
                                        color: #1976d2;
                                        cursor: pointer;
                                        font-size: 10px;
                                        word-break: break-word;
                                        display: block;
                                        width: 100%;
                                    "
                                >
                                    {{
                                        relatedInvoice.consecutive ||
                                            relatedInvoice.fileName
                                    }}
                                </a>
                            </div>
                            <div
                                v-if="
                                    getRelatedInvoicesForEntry(item).length ===
                                        0
                                "
                                style="min-height: 20px;"
                            >
                                <span class="text-caption text--secondary">
                                    No related invoices
                                </span>
                            </div>
                        </div>
                    </template>
                </v-data-table>
            </v-card-text>
            <v-card-actions>
                <v-btn
                    color="primary"
                    text
                    @click="$emit('openPurchase')"
                    class="mr-2"
                >
                    <v-icon left small>mdi-open-in-new</v-icon>
                    Open Purchase
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn color="grey" text @click="$emit('close')">
                    Cancel
                </v-btn>
                <v-btn
                    color="primary"
                    @click="handleRelate"
                    :loading="relateLoading"
                    :disabled="isInvoiceRejected"
                >
                    Relate
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- Quantity Selection Dialog -->
        <v-dialog
            v-model="quantitySelectionDialog"
            max-width="500px"
            persistent
        >
            <v-card>
                <v-card-title class="headline">
                    Enter the quantity to select
                </v-card-title>
                <v-card-text>
                    <div class="mb-4">
                        <p class="text-body-1 mb-2">
                            <strong>Available quantity:</strong>
                            {{ availableQuantity }}
                        </p>
                        <p class="text-caption text--secondary">
                            Total quantity: {{ currentEntryQuantity }}
                        </p>
                    </div>
                    <v-text-field
                        v-model.number="selectedQuantity"
                        label="Quantity"
                        type="number"
                        :min="0"
                        :max="currentEntryQuantity"
                        :rules="quantityRules"
                        outlined
                        dense
                    ></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="grey" text @click="closeQuantityDialog">
                        Cancel
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        @click="saveQuantitySelection"
                        :disabled="!isQuantityValid"
                    >
                        {{
                            currentEntry &&
                            isEntrySelected(currentEntry.entryId) &&
                            Number(selectedQuantity) === 0
                                ? 'Unselect'
                                : 'Save'
                        }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
import moment from 'moment'
import API from '@/services/api'

export default {
    name: 'RelateInvoiceToEntries',
    props: {
        dialog: {
            type: Boolean,
            default: false,
        },
        invoice: {
            type: Object,
            default: null,
        },
        purchase: {
            type: Object,
            default: null,
        },
        companyId: {
            type: String,
            required: true,
        },
        checkWithPoAccountingPermission: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedEntriesForInvoice: {}, // { entryId: selectedQty }
            relateLoading: false,
            entrySapStates: {},
            entryAccountantStates: {},

            // Variables para el diálogo de selección de cantidad
            quantitySelectionDialog: false,
            currentEntry: null,
            selectedQuantity: 0,
            itemFilter: '',
            supplierFilter: '',
            sortBy: null,
            sortDesc: true,
        }
    },
    computed: {
        isInvoiceRejected() {
            return !!this.invoice?.rejected
        },
        entryRelationHeaders() {
            return [
                {
                    text: 'Select',
                    value: 'select',
                    sortable: false,
                    width: '80px',
                },
                {
                    text: 'Item Description',
                    value: 'itemDescription',
                    sortable: false,
                    width: '240px',
                },
                {
                    text: 'Supplier',
                    value: 'supplier',
                    sortable: false,
                    width: '180px',
                },
                {
                    text: 'Entry Quantity',
                    value: 'entryQuantity',
                    sortable: false,
                    width: '120px',
                },
                {
                    text: 'Selected Qty',
                    value: 'selectedQuantity',
                    sortable: false,
                    width: '120px',
                },
                {
                    text: 'Available Qty',
                    value: 'availableQuantity',
                    sortable: false,
                    width: '120px',
                },
                {
                    text: 'Unit Price',
                    value: 'unitPrice',
                    sortable: false,
                    width: '140px',
                },
                {
                    text: 'Entry Date',
                    value: 'entryDate',
                    sortable: false,
                    width: '150px',
                },
                {
                    text: 'SAP',
                    value: 'sap',
                    sortable: false,
                    width: '80px',
                },
                {
                    text: 'ACCOUNTANT',
                    value: 'accountant',
                    sortable: false,
                    width: '80px',
                },
                {
                    text: 'Related Invoices',
                    value: 'relatedInvoices',
                    sortable: false,
                    width: '350px',
                },
            ]
        },
        allEntriesForPurchase() {
            if (!this.purchase || !this.purchase.items) {
                return []
            }

            let allEntries = []
            this.purchase.items.forEach(item => {
                if (item.entries && item.entries.length > 0) {
                    item.entries.forEach(entry => {
                        const entryQuantity = entry.quantity || 0
                        const allocatedQuantity = this.getTotalAllocatedQtyForEntry(
                            entry.id
                        )
                        const availableQuantity = Math.max(
                            0,
                            entryQuantity - allocatedQuantity
                        )

                        // Get unit price
                        const unitPrice =
                            item.quotes?.find(quote => quote.able == true)
                                ?.price || 0
                        allEntries.push({
                            entryId: entry.id,
                            itemId: item.id,
                            itemDescription: item.description,
                            supplierName: this.getSupplierName(item),
                            entryQuantity: entryQuantity,
                            availableQuantity: availableQuantity,
                            selectedQuantity: allocatedQuantity,
                            entryDate: entry.date || entry.createdOn,
                            sap: entry.sap || false,
                            entry: entry,
                            item: item,
                            unitPrice: unitPrice,
                        })
                    })
                }
            })
            return allEntries
        },
        filteredEntries() {
            const itemQuery = (this.itemFilter || '').trim().toLowerCase()
            const supplierQuery = (this.supplierFilter || '')
                .trim()
                .toLowerCase()

            return this.allEntriesForPurchase.filter(entry => {
                if (
                    itemQuery &&
                    !String(entry.itemDescription || '')
                        .toLowerCase()
                        .includes(itemQuery)
                ) {
                    return false
                }
                if (
                    supplierQuery &&
                    !String(entry.supplierName || '')
                        .toLowerCase()
                        .includes(supplierQuery)
                ) {
                    return false
                }
                return true
            })
        },
        visibleEntries() {
            const items = this.filteredEntries.slice()
            if (!this.sortBy) {
                return items
            }

            const column = this.sortBy
            const desc = this.sortDesc
            items.sort((a, b) => {
                const left = this.sortValue(a, column)
                const right = this.sortValue(b, column)
                if (left === right) return 0
                if (desc) return left > right ? -1 : 1
                return left > right ? 1 : -1
            })
            return items
        },
        availableQuantity() {
            if (!this.currentEntry) return 0
            const entryQuantity = this.currentEntry.entryQuantity || 0

            return Math.max(
                0,
                entryQuantity -
                    this.getTotalAllocatedQtyForEntry(
                        this.currentEntry.entryId,
                        true
                    )
            )
        },
        currentEntryQuantity() {
            if (!this.currentEntry) return 0
            return this.currentEntry.entryQuantity || 0
        },
        isQuantityValid() {
            if (
                this.selectedQuantity === null ||
                this.selectedQuantity === undefined ||
                this.selectedQuantity === ''
            ) {
                return false
            }
            const quantity = Number(this.selectedQuantity)

            // Allow 0 if entry is already selected (to unselect)
            if (quantity === 0) {
                return (
                    this.currentEntry &&
                    this.isEntrySelected(this.currentEntry.entryId)
                )
            }

            // For quantities > 0, validate range
            return (
                quantity > 0 &&
                quantity <= this.currentEntryQuantity &&
                quantity <= this.availableQuantity
            )
        },
        quantityRules() {
            return [
                v => {
                    if (v === null || v === undefined || v === '') {
                        return 'Quantity is required'
                    }
                    const quantity = Number(v)
                    if (isNaN(quantity)) {
                        return 'Must be a valid number'
                    }
                    if (quantity < 0) {
                        return 'Quantity cannot be less than 0'
                    }
                    // Allow 0 if entry is already selected (to unselect)
                    if (quantity === 0) {
                        if (
                            this.currentEntry &&
                            this.isEntrySelected(this.currentEntry.entryId)
                        ) {
                            return true
                        }
                        return 'Quantity must be greater than 0'
                    }
                    if (quantity > this.currentEntryQuantity) {
                        return `Quantity cannot be greater than ${this.currentEntryQuantity}`
                    }
                    if (quantity > this.availableQuantity) {
                        return `Quantity cannot be greater than available (${this.availableQuantity})`
                    }
                    return true
                },
            ]
        },
    },
    watch: {
        dialog(newVal) {
            if (newVal) {
                this.initializeDialog()
            } else {
                this.resetDialog()
            }
        },
    },
    methods: {
        getPurchaseInvoicesList() {
            if (
                this.purchase?.purchaseInvoices &&
                this.purchase.purchaseInvoices.length > 0
            ) {
                return this.purchase.purchaseInvoices
            }
            if (Array.isArray(this.purchase?.invoices)) {
                return this.purchase.invoices
            }
            return []
        },
        getTotalAllocatedQtyForEntry(entryId, excludeCurrentInvoice = false) {
            let totalSelectedQty = 0
            const purchaseInvoices = this.getPurchaseInvoicesList()

            purchaseInvoices.forEach(invoice => {
                if (invoice.rejected) {
                    return
                }
                if (
                    this.invoice &&
                    invoice.fileName === this.invoice.fileName
                ) {
                    return
                }

                ;(invoice.relatedEntries || []).forEach(relatedEntry => {
                    if (relatedEntry.entryId === entryId) {
                        totalSelectedQty += relatedEntry.selectedQty || 0
                    }
                })
            })

            if (
                !excludeCurrentInvoice &&
                this.selectedEntriesForInvoice &&
                entryId in this.selectedEntriesForInvoice
            ) {
                totalSelectedQty += this.selectedEntriesForInvoice[entryId] || 0
            }

            return totalSelectedQty
        },
        initializeDialog() {
            // Initialize with entries already related to this invoice
            this.selectedEntriesForInvoice = {}
            if (this.invoice && this.invoice.relatedEntries) {
                this.invoice.relatedEntries.forEach(entry => {
                    this.selectedEntriesForInvoice[entry.entryId] =
                        entry.selectedQty || 0
                })
            }

            // Initialize SAP and Accountant states for each entry
            this.entrySapStates = {}
            this.entryAccountantStates = {}
            const frozenByEntry = {}
            if (
                this.invoice?.rejected &&
                Array.isArray(this.invoice.frozenChecks?.entries)
            ) {
                this.invoice.frozenChecks.entries.forEach(frozenEntry => {
                    frozenByEntry[frozenEntry.entryId] = frozenEntry
                })
            }
            if (this.purchase && this.purchase.items) {
                this.purchase.items.forEach(item => {
                    if (item.entries && item.entries.length > 0) {
                        item.entries.forEach(entry => {
                            const frozenEntry = frozenByEntry[entry.id]
                            this.entrySapStates[entry.id] = frozenEntry
                                ? !!frozenEntry.sap
                                : entry.sap || false
                            this.entryAccountantStates[entry.id] = frozenEntry
                                ? !!frozenEntry.accountant
                                : entry.accountant || false
                        })
                    }
                })
            }
        },
        resetDialog() {
            this.selectedEntriesForInvoice = {}
            this.entrySapStates = {}
            this.entryAccountantStates = {}
            this.quantitySelectionDialog = false
            this.currentEntry = null
            this.selectedQuantity = 0
            this.itemFilter = ''
            this.supplierFilter = ''
            this.sortBy = null
            this.sortDesc = true
        },
        toggleSort(column) {
            if (this.sortBy !== column) {
                this.sortBy = column
                this.sortDesc = true
                return
            }
            if (this.sortDesc) {
                this.sortDesc = false
                return
            }
            this.sortBy = null
            this.sortDesc = true
        },
        sortIcon(column) {
            if (this.sortBy !== column) return 'mdi-sort'
            return this.sortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up'
        },
        sortValue(entry, column) {
            if (column === 'entryDate') {
                return this.entryDateValue(entry.entryDate)
            }
            if (column === 'unitPrice') {
                return Number(entry.unitPrice) || 0
            }
            return 0
        },
        entryDateValue(dateValue) {
            if (!dateValue) return 0
            if (typeof dateValue === 'object') {
                const seconds = dateValue.seconds ?? dateValue._seconds
                if (seconds != null) return Number(seconds) * 1000
            }
            const parsed = moment(dateValue)
            return parsed.isValid() ? parsed.valueOf() : 0
        },
        getSupplierName(item) {
            if (!item.quotes || item.quotes.length === 0) {
                return 'No supplier'
            }

            const enabledQuote = item.quotes.find(quote => quote.able === true)

            if (enabledQuote && enabledQuote.supplier) {
                return enabledQuote.supplier.name
            }

            const firstQuote = item.quotes[0]
            if (firstQuote && firstQuote.supplier) {
                return firstQuote.supplier.name
            }

            return 'No supplier'
        },
        formatEntryDate(dateValue) {
            if (!dateValue) return 'N/A'

            // Si es un timestamp de Firestore
            if (dateValue.seconds) {
                return moment.unix(dateValue.seconds).format('YYYY-MM-DD')
            }

            // Si es una fecha ISO string
            if (typeof dateValue === 'string') {
                return moment(dateValue).format('YYYY-MM-DD')
            }

            return 'N/A'
        },
        getRelatedInvoicesForEntry(entry) {
            const purchaseInvoices = this.getPurchaseInvoicesList()
            if (purchaseInvoices.length === 0) {
                return []
            }

            return purchaseInvoices.filter(invoice => {
                return (
                    invoice.relatedEntries &&
                    invoice.relatedEntries.some(
                        relatedEntry => relatedEntry.entryId === entry.entryId
                    )
                )
            })
        },
        isEntrySelected(entryId) {
            return entryId in this.selectedEntriesForInvoice
        },
        selectCheckboxColor(item) {
            if (this.isInvoiceRejected) {
                return 'grey lighten-1'
            }
            return this.isEntrySelected(item.entryId)
                ? 'primary'
                : 'grey lighten-1'
        },
        openQuantityDialog(item) {
            if (this.isInvoiceRejected) {
                return
            }
            this.currentEntry = item
            // If entry is already selected, use its selectedQty, otherwise use available quantity
            if (this.isEntrySelected(item.entryId)) {
                this.selectedQuantity =
                    this.selectedEntriesForInvoice[item.entryId] || 0
            } else {
                const available = item.availableQuantity || 0
                this.selectedQuantity = available > 0 ? available : 0
            }
            this.quantitySelectionDialog = true
        },
        closeQuantityDialog() {
            this.quantitySelectionDialog = false
            this.currentEntry = null
            this.selectedQuantity = 0
        },
        saveQuantitySelection() {
            if (!this.isQuantityValid || !this.currentEntry) {
                return
            }

            const quantity = Number(this.selectedQuantity)

            if (quantity === 0) {
                // Unselect the entry
                delete this.selectedEntriesForInvoice[this.currentEntry.entryId]
            } else {
                // Set the selected quantity
                this.$set(
                    this.selectedEntriesForInvoice,
                    this.currentEntry.entryId,
                    quantity
                )
            }

            this.closeQuantityDialog()
        },
        sapCheckboxValue(entry) {
            return !!this.entrySapStates[entry.entryId]
        },
        accountantCheckboxValue(entry) {
            return !!this.entryAccountantStates[entry.entryId]
        },
        async updateEntrySapStatus(entry, isChecked) {
            if (this.isInvoiceRejected) {
                return
            }
            try {
                await API.updatePurchaseEntrySap({
                    purchaseId: this.purchase.id,
                    itemId: entry.itemId,
                    entryId: entry.entryId,
                    sap: isChecked,
                })

                // Actualizar el estado local del entry
                entry.entry.sap = isChecked

                // Actualizar el estado local del checkbox
                this.$set(this.entrySapStates, entry.entryId, isChecked)

                // Forzar actualización de la vista
                this.$forceUpdate()
            } catch (error) {
                // Revertir el estado local en caso de error
                this.$set(this.entrySapStates, entry.entryId, !isChecked)

                this.$emit('error', {
                    message: `Error updating SAP status: ${error.message}`,
                })
            }
        },
        async updateEntryAccountantStatus(entry, isChecked) {
            if (this.isInvoiceRejected) {
                return
            }
            try {
                await API.updatePurchaseEntryAccountant({
                    purchaseId: this.purchase.id,
                    itemId: entry.itemId,
                    entryId: entry.entryId,
                    accountant: isChecked,
                })

                // Actualizar el estado local del entry
                entry.entry.accountant = isChecked

                // Actualizar el estado local del checkbox
                this.$set(this.entryAccountantStates, entry.entryId, isChecked)

                // Forzar actualización de la vista
                this.$forceUpdate()
            } catch (error) {
                // Revertir el estado local en caso de error
                this.$set(this.entryAccountantStates, entry.entryId, !isChecked)

                this.$emit('error', {
                    message: `Error updating accountant status: ${error.message}`,
                })
            }
        },
        async handleRelate() {
            if (this.isInvoiceRejected) {
                return
            }
            if (!this.invoice || !this.purchase) {
                this.$emit('error', {
                    message:
                        'Please select at least one entry to relate the invoice',
                })
                return
            }

            try {
                this.relateLoading = true

                const relatedEntries = Object.keys(
                    this.selectedEntriesForInvoice
                ).map(entryId => {
                    let foundItem = null

                    this.purchase.items.forEach(item => {
                        if (item.entries && item.entries.length > 0) {
                            const entry = item.entries.find(
                                e => e.id === entryId
                            )
                            if (entry) {
                                foundItem = item
                            }
                        }
                    })

                    return {
                        entryId: entryId,
                        itemId: foundItem ? foundItem.id : 'unknown',
                        selectedQty:
                            this.selectedEntriesForInvoice[entryId] || 0,
                    }
                })

                const purchaseInvoices = this.getPurchaseInvoicesList()
                const invoiceIndex = purchaseInvoices.findIndex(
                    inv => inv.fileName === this.invoice.fileName
                )

                if (invoiceIndex !== -1) {
                    purchaseInvoices[
                        invoiceIndex
                    ].relatedEntries = relatedEntries
                }

                if (!this.purchase.purchaseInvoices) {
                    this.$set(
                        this.purchase,
                        'purchaseInvoices',
                        purchaseInvoices
                    )
                }

                await API.updatePurchaseInvoices({
                    companyId: this.companyId,
                    purchaseId: this.purchase.id,
                    invoices: purchaseInvoices,
                })

                this.$forceUpdate()

                this.$emit('relate', {
                    purchase: this.purchase,
                    invoice: this.invoice,
                })
            } catch (error) {
                this.$emit('error', {
                    message: `Error relating invoice to entries: ${error.message}`,
                })
            } finally {
                this.relateLoading = false
            }
        },
        formatNumber(number) {
            return new Intl.NumberFormat('de-DE').format(
                Math.ceil(number != undefined ? number : 0)
            )
        },
    },
}
</script>
